/**
 * データストア
 *
 * works / events のキャッシュと O(1) slug 検索を提供する Zustand ストア。
 * 一覧ページ・詳細ページ間でデータを共有し、不要な再fetchを防ぐ。
 *
 * @see src/lib/data-loader.ts - データ読み込み関数
 * @see src/types/domain.ts - Work, Event 型定義
 */

import { create } from 'zustand';
import type { Work, Event, ChronologyEntry } from '@/types/domain';
import {
  loadWorksData,
  loadEventsData,
  loadChronologyData,
  generateSlug,
} from '@/lib/data-loader';

/**
 * データストアの状態型
 */
export interface DataState {
  // --- データ本体 ---

  /** 作品データ配列 */
  works: Work[];

  /** イベントデータ配列 */
  events: Event[];

  /** 年表データ配列 */
  chronology: ChronologyEntry[];

  // --- ロード状態 ---

  /** 作品データがロード済みか */
  worksLoaded: boolean;

  /** イベントデータがロード済みか */
  eventsLoaded: boolean;

  /** 年表データがロード済みか */
  chronologyLoaded: boolean;

  /** 作品データのロード中フラグ（表示用） */
  isLoadingWorks: boolean;

  /** イベントデータのロード中フラグ（表示用） */
  isLoadingEvents: boolean;

  /** 年表データのロード中フラグ（表示用） */
  isLoadingChronology: boolean;

  /** 作品データのロードエラー */
  worksError: string | null;

  /** イベントデータのロードエラー */
  eventsError: string | null;

  /** 年表データのロードエラー */
  chronologyError: string | null;

  // --- アクション ---

  /**
   * 作品データを取得
   * キャッシュ済みなら即return、進行中なら同一Promiseに合流（single-flight）
   */
  fetchWorks: () => Promise<void>;

  /**
   * イベントデータを取得
   * キャッシュ済みなら即return、進行中なら同一Promiseに合流（single-flight）
   */
  fetchEvents: () => Promise<void>;

  /**
   * 年表データを取得
   * キャッシュ済みなら即return、進行中なら同一Promiseに合流（single-flight）
   */
  fetchChronology: () => Promise<void>;

  // --- slug 索引（Map） ---

  /** slug → Work の O(1) 索引 */
  worksBySlug: Map<string, Work>;

  /** slug → Event の O(1) 索引 */
  eventsBySlug: Map<string, Event>;

  // --- slug 検索 ---

  /**
   * slug から作品を O(1) で検索
   * @param slug - URLスラッグ（UI用JSONではidと同じ）
   */
  findWorkBySlug: (slug: string) => Work | undefined;

  /**
   * slug からイベントを O(1) で検索
   * @param slug - URLスラッグ（UI用JSONではidと同じ）
   */
  findEventBySlug: (slug: string) => Event | undefined;
}

// --- single-flight 用 Promise（モジュールスコープ） ---
let worksPromise: Promise<void> | null = null;
let eventsPromise: Promise<void> | null = null;
let chronologyPromise: Promise<void> | null = null;

/**
 * データストア
 *
 * 使用例:
 * ```typescript
 * import { useDataStore } from '@/stores/data-store';
 *
 * function WorkDetail() {
 *   const { worksLoaded, fetchWorks, findWorkBySlug } = useDataStore();
 *
 *   useEffect(() => {
 *     if (!worksLoaded) fetchWorks();
 *   }, [worksLoaded, fetchWorks]);
 *
 *   const work = findWorkBySlug(slug);
 * }
 * ```
 */
export const useDataStore = create<DataState>((set, get) => ({
  // 初期状態
  works: [],
  events: [],
  chronology: [],
  worksBySlug: new Map(),
  eventsBySlug: new Map(),
  worksLoaded: false,
  eventsLoaded: false,
  chronologyLoaded: false,
  isLoadingWorks: false,
  isLoadingEvents: false,
  isLoadingChronology: false,
  worksError: null,
  eventsError: null,
  chronologyError: null,

  fetchWorks: async () => {
    if (get().worksLoaded) return;
    if (worksPromise) return worksPromise;

    worksPromise = (async () => {
      set({ isLoadingWorks: true, worksError: null });
      try {
        const data = await loadWorksData();
        const worksBySlug = new Map(data.map((w) => [generateSlug(w.id), w]));
        set({ works: data, worksBySlug, worksLoaded: true });
      } catch (error) {
        set({
          worksError: error instanceof Error ? error.message : String(error),
        });
      } finally {
        set({ isLoadingWorks: false });
        worksPromise = null;
      }
    })();

    return worksPromise;
  },

  fetchEvents: async () => {
    if (get().eventsLoaded) return;
    if (eventsPromise) return eventsPromise;

    eventsPromise = (async () => {
      set({ isLoadingEvents: true, eventsError: null });
      try {
        const data = await loadEventsData();
        const eventsBySlug = new Map(data.map((e) => [generateSlug(e.id), e]));
        set({ events: data, eventsBySlug, eventsLoaded: true });
      } catch (error) {
        set({
          eventsError: error instanceof Error ? error.message : String(error),
        });
      } finally {
        set({ isLoadingEvents: false });
        eventsPromise = null;
      }
    })();

    return eventsPromise;
  },

  fetchChronology: async () => {
    if (get().chronologyLoaded) return;
    if (chronologyPromise) return chronologyPromise;

    chronologyPromise = (async () => {
      set({ isLoadingChronology: true, chronologyError: null });
      try {
        const data = await loadChronologyData();
        set({ chronology: data, chronologyLoaded: true });
      } catch (error) {
        set({
          chronologyError:
            error instanceof Error ? error.message : String(error),
        });
      } finally {
        set({ isLoadingChronology: false });
        chronologyPromise = null;
      }
    })();

    return chronologyPromise;
  },

  findWorkBySlug: (slug: string) => get().worksBySlug.get(slug),

  findEventBySlug: (slug: string) => get().eventsBySlug.get(slug),
}));
